define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi",
	"csi/util",
	"csi/xml"
], function(array, declare, lang, csi, util, xml) {

	var ATTACH = "Attach";
	var CHANGE_ATTRIBUTE = "ChangeAttribute";
	var COMMENT_TEMP = "CommentTemp";
	var PUBLISH_ATTRIBUTE = "PublishAttribute";
	var PARAMETER = "Parameter";
	var VALUE_OP = "ValueOp";

	return declare(null, {
		constructor: function(writer, e) {
			this.writer = writer;
			this.element = e;
		},

		getURL: function() {
			return this.element.getAttribute(xml.PROP_URL);
		},

		addAttribute: function(id, type, value, flags, exp) {
			var node = xml.e(this.writer.doc, PUBLISH_ATTRIBUTE, this.element);
			this._outputAttribute(node, id, type, value, flags, exp);
			return this;
		},

		/**
		 * Adds a Parameter node to the ChangeNode request
		 * @param {string} id - ID of parameter to add to request
		 * @param {string} type - variable type of the parameter
		 * @param {object} value - Value for parameter
		 * @return {ChangeNode} - reference to this object
		 */
		addParameter: function(id, type, value) {
			var node = xml.e(this.writer.doc, PARAMETER, this.element);
			this._outputAttribute(node, id, type, value);
			return this;
		},

		/**
		 * Add operation to reference the current node from another in the relationship with the given id.
		 * If the relationship is a container (list, set, vector), then the current node will be appended to the container.
		 * If the relationship is a ref, then the current node will replace any existing value.
		 * This operation is usually called after any calls to changeAttribute, so that any OnChange custom action on the
		 * given relationship will have access to the node that includes all attribute changes.
		 * @param {string} url - URL to node that will reference the current node
		 * @param {string} id - relationship id
		 * @param {string} [key] - key required for refmap attributes, otherwise optional
		 * @return {ChangeNode} - this
		 */
		attach: function(url, id, key) {
			var node = xml.e(this.writer.doc, ATTACH, this.element);
			node.setAttribute(xml.PROP_URL, url);
			node.setAttribute(xml.PROP_ID, id);

			if (key != null) {
				node.setAttribute(xml.PROP_KEY, key);
			}

			return this;
		},

		beginComment: function(prefixComment) {
			// generate secondary node for element to contain any subsequent nodes for the Comment
			this.element = xml.e(this.writer.doc, COMMENT_TEMP, this.element);
			if (prefixComment) {
				this.comment(prefixComment);
			}
			return this;
		},

		cancelChanges: function() {
			// remove changes to node and set as cannot cache so that old node state will be returned immediately if caching changes as we go
			xml.removeChildren(this.element);

			if (this.writer.cache) {
				this.writer._cannotCacheChange();
			}
		},

		changeAttribute: function(id, type, value, bAppend, bRemove, flags, exp) {
			this._changeAttribute(id, type, value, bAppend, bRemove, flags, exp);
			return this;
		},

		changeAttributeExp: function(id, exp, bOneTime) {
			// summary: add one-time expression to attribute - if one time, then it should be published and evaluated immediately
			this._changeAttribute(id, null, null, null, null, null, exp);

			if (bOneTime) {
				this.changeAttributeFlag(id, xml.ATTR_FLAG_EVAL_EXPRESSION | xml.ATTR_FLAG_ONE_TIME_EXPRESSION, true);
				this.writer._cannotCacheChange();
			}

			return this;
		},

		/**
		 * change the Exp and ExpOp on the attribute
		 * @param {string} id - id of the attribute
		 * @param {string} expOp - None, Replace, ChangeIfExist, Restore, OneTimeIfNone
		 * @param {string} [exp] - expression of the attribute
		 * @returns {ChangeNode} - this
		 */
		changeAttributeExpOp: function(id, expOp, exp) {
			var node = this._changeAttribute(id);

			node.setAttribute(xml.PROP_EXP_OP, expOp);

			if (exp != null) {
				node.setAttribute(xml.PROP_EXP, exp);
			}

			// the client does not have the logic to do this on its own, so need to send operation to server
			this.writer._cannotCacheChange();
			return this;
		},

		changeAttributeFlag: function(id, flags, bAdd, bDelete) {
			// do nothing if zero
			if (!flags) return this;

			var node = xml.e(this.writer.doc, CHANGE_ATTRIBUTE, this.element);
			var clientNode = this._getClientNode(id);
			if (clientNode) {
				// if cached element, then compute result of change and apply to attribute
				var fg = flags;

				if (bAdd) {
					fg |= xml.nodeAttributeGetFlags(clientNode, id);
				} else if (bDelete) {
					fg = xml.nodeAttributeGetFlags(clientNode, id) & ~fg;
				}

				xml.nodeAttributeSetFlags(clientNode, id, fg);
			} else {
				this.writer._cannotCacheChange();
			}

			// if no cached element, then just output simple operation
			if (bAdd) {
				node.setAttribute("AddFG", flags);
			} else if (bDelete) {
				node.setAttribute("DelFG", flags);
			} else {
				node.setAttribute(xml.PROP_FG, flags);
			}

			node.setAttribute(xml.PROP_ID, id);
			return this;
		},

		changeAttributeKeys: function(/*String*/id, /*String*/type, /*Object*/keyMap, /*Boolean*/bInsert) {
			// supports ValueOp=SetKey or Insert for container types (i.e. map, vector)
			var doc = this.writer.doc;
			var typeBase = xml.attributeTypeGetBase(type);
			var bMap = xml.attributeTypeIsMap(type);
			var node = xml.e(doc, CHANGE_ATTRIBUTE, this.element);
			var clientNode = this._getClientNode(id);
			var value = clientNode ? clientNode[id] : null;
			var n = 0;
			var child;

			node.setAttribute(xml.PROP_ID, id);
			node.setAttribute(xml.PROP_TYPE, typeBase + xml.ATTR_MAP);
			node.setAttribute(VALUE_OP, bInsert ? "Insert" : "SetKey");

			csi.applyForEach(keyMap, function(val, key) {
				child = xml.e(doc, typeBase, node);
				child.appendChild(doc.createTextNode(String(val)));
				child.setAttribute(xml.PROP_KEY, key);

				if (clientNode) {
					if (bMap || !bInsert) {
						value[key] = val;
					} else {
						value.splice(key, 0, val);
					}
				}

				++n;
			});

			if (!clientNode) {
				this.writer._cannotCacheChange();
			}

			this.writer.setChanged(true);
			return this;
		},

		copyAttributeValue: function(node, id) {
			var type = xml.nodeAttributeGetType(node, id);
			if (type) {
				var v = node[id];
				if (v != null) {
					this._changeAttribute(id, type, v);
				}
			}

			return this;
		},

		/**
		 * Add comment inside operation
		 * @param {string} text 
		 * @return {this}
		 */
		comment(text) {
			const {element, writer: {doc}} = this;
			element.appendChild(doc.createTextNode("\n"));
			element.appendChild(doc.createComment(text));
			return this;
		},

		deleteAttribute: function(id) {
			var node = xml.e(this.writer.doc, "DeleteAttribute", this.element);
			node.setAttribute("Id", id);
			this.writer._cannotCacheChange();
			return this;
		},

		dependencyPath: function(/*String*/path) {
			// summary: write operation to pre-load nodes reachable from the path
			var e = xml.e(this.writer.doc, "DependencyPath", this.element);
			e.appendChild(this.writer.doc.createTextNode(path));
			return this;
		},

		disableAttributeExp: function(id) {
			// summary: write operation to disable expression of given attribute
			this.changeAttributeFlag(id, xml.ATTR_FLAG_DISABLE_EXPRESSION, true, false);
			this.changeAttributeFlag(id, xml.ATTR_FLAG_EVAL_EXPRESSION, false, true);
			return this;
		},

		endComment: function() {
			// element should be a CommentTemp node, so ensure that is the case
			const { element, writer: {doc}} = this;
			// only do this operation if current element is a CommentTemp node
			if (element.tagName != COMMENT_TEMP) {
				throw new Error("Cannot call endComment() prior to calling beginComment()");
			}

			// re-align to parent node, remove child node and generate comment accordingly
			this.element = element.parentElement;
			element.parentElement.removeChild(element);
			// if there are any text nodes, extract them and render to a separate string
			let prefixComment = "", lastChildComment = false, nodesToRemove = [];
			element.childNodes.forEach((child) => {
				if (child.nodeType === 8 && child.nodeName === "#comment") {
					// extract any comment text prior to render so it can be positioned in the comment accordingly
					if (prefixComment !== "") {
						prefixComment += '\t'; 
					}
					prefixComment += child.textContent;
					nodesToRemove.push(child);
					lastChildComment = true;
				} else if (child.nodeType === 3 && lastChildComment) {
					// capture any text nodes as raw text for the previous comment and prune these nodes
					prefixComment += child.textContent;
					
					nodesToRemove.push(child);
					lastChildComment = false;
				}
			});
			nodesToRemove.forEach((child) => {
				element.removeChild(child);
			});
			return this.comment(`${prefixComment??'\n'}${element.innerHTML}\n`);
		},

		moveAttributeKey: function(id, type, keyFrom, keyTo) {
			// summary: supports ValueOp=ChangeKey for container types (i.e. map, vector)
			var doc = this.writer.doc;
			var bMap = xml.attributeTypeIsMap(type);
			var typeKey = bMap ? xml.ATTR_STRING : xml.ATTR_INTEGER;
			var node = xml.e(doc, CHANGE_ATTRIBUTE, this.element);
			var clientNode = this._getClientNode(id);
			var value = clientNode ? clientNode[id] : null;

			node.setAttribute(xml.PROP_ID, id);
			node.setAttribute(xml.PROP_TYPE, typeKey + xml.ATTR_MAP);
			node.setAttribute(VALUE_OP, "ChangeKey");

			var child = xml.e(doc, typeKey, node);
			child.setAttribute(xml.PROP_KEY, "From");
			child.appendChild(doc.createTextNode(keyFrom));

			child = xml.e(doc, typeKey, node);
			child.setAttribute(xml.PROP_KEY, "To");
			child.appendChild(doc.createTextNode(keyTo));

			if (clientNode) {
				if (bMap) {
					value[keyTo] = value.remove(keyFrom);
				} else {
					value.splice(keyTo + ((keyFrom < keyTo) ? 1 : 0), 0, value[keyFrom]);
					value.splice(keyFrom + ((keyTo < keyFrom) ? 1 : 0), 1);
				}
			} else {
				this.writer._cannotCacheChange();
			}

			this.writer.setChanged(true);
			return this;
		},

		publishAttribute: function(id, type, value, bAppend, bRemove, flags, exp) {
			this._attributeOperation(PUBLISH_ATTRIBUTE, id, type, value, bAppend, bRemove, flags, exp);
			return this;
		},

		removeAttributeKeys: function(id, type, keys) {
			// supports ValueOp=RemoveKey for container types (i.e. map, vector)
			var doc = this.writer.doc;
			var bMap = xml.attributeTypeIsMap(type);
			var typeKey = bMap ? xml.ATTR_STRING : xml.ATTR_INTEGER;
			var node = xml.e(doc, CHANGE_ATTRIBUTE, this.element);
			var clientNode = this._getClientNode(id);
			var value = clientNode ? clientNode[id] : null;

			node.setAttribute(xml.PROP_ID, id);
			node.setAttribute(xml.PROP_TYPE, typeKey + xml.ATTR_LIST);
			node.setAttribute(VALUE_OP, "RemoveKey");

			// sort keys in reverse order so removing one will not affect the other if caching
			if (clientNode && !bMap && (keys.length > 1)) {
				keys.sort(function(a, b) {return a - b;}).reverse();
			}

			array.forEach(keys, function(key) {
				xml.e(doc, typeKey, node).appendChild(doc.createTextNode(key));

				if (clientNode) {
					if (bMap) {
						delete value[key];
					} else {
						value.splice(key, 1);
					}
				}
			});

			if (!clientNode) {
				this.writer._cannotCacheChange();
			}

			this.writer.setChanged(true);
			return this;
		},

		restoreAttributeExp: function(id) {
			// summary: restore expression on the attribute to the one that is defined in the reflection and evaluate
			this.changeAttributeFlag(id, xml.ATTR_FLAG_DISABLE_EXPRESSION, false, true);

			return this.changeAttributeExpOp(id, "Restore");
		},

		searchAndReplace: function(id, search, replace, bKeys) {
			var node = xml.e(this.writer.doc, CHANGE_ATTRIBUTE, this.element);
			node.setAttribute("Id", id);
			node.setAttribute("Search", search);
			node.setAttribute("Replace", replace || "");

			if (bKeys) {
				node.setAttribute("ValueOp", "SearchAndReplaceKeys");
			}
			return this;
		},

		_changeAttribute: function(id, type, value, bAppend, bRemove, flags, exp) {
			return this._attributeOperation(CHANGE_ATTRIBUTE, id, type, value, bAppend, bRemove, flags, exp);
		},

		_attributeOperation: function(operationName, id, type, value, bAppend, bRemove, flags, exp) {
			var node = xml.e(this.writer.doc, operationName, this.element);

			this._outputAttribute(node, id, type, value, flags, exp, bAppend, bRemove);

			if (bAppend) {
				node.setAttribute(VALUE_OP, "Append");
			} else if (bRemove) {
				node.setAttribute(VALUE_OP, "Remove");
			}

			return node;
		},

		_getClientNode: function(id, bForce) {
			// summary: get node on which change will be applied
			var node = this.writer.getCachedNode(this.getURL());

			if (node && id && !xml.nodeHasAttribute(node, id) && bForce) {
				// add attribute to cache if not already there
				xml.nodeCreateClientAttr(node, id, null, null, 0);
			}

			return node;
		},

		_outputAttribute: function(node, id, type, value, flags, exp, bAppend, bRemove) {
			var clientNode = this._getClientNode(id, true);
			var doc = this.writer.doc;

			if (!clientNode || bRemove) {
				this.writer._cannotCacheChange();
			}

			node.setAttribute(xml.PROP_ID, id);

			if (flags != null) {
				node.setAttribute(xml.PROP_FG, flags);
				if (clientNode) xml.nodeAttributeSetFlags(clientNode, id, flags);
			}

			if (exp != null) {
				node.setAttribute(xml.PROP_EXP, exp);
			}

			if (type && (value != null)) {
				node.setAttribute(xml.PROP_TYPE, type);
				if (clientNode) xml.nodeAttributeSetType(clientNode, id, type);

				// determine if value is container (list, map, vector) and if so, what type
				var typeBase = xml.attributeTypeGetBase(type);
				if (typeBase) {
					// output XML operations to change container attribute
					var bHash = !(value instanceof Array);

					if (!bAppend) {
						xml.removeChildren(node);
					}

					if (bHash) {
						csi.applyForEach(value, function(v, key) {
							var e = xml.e(doc, typeBase, node);
							e.setAttribute(xml.PROP_KEY, key);
							e.appendChild(doc.createTextNode(String(v)));
						});
					} else {
						array.forEach(value, function(v, key) {
							xml.e(doc, typeBase, node).appendChild(doc.createTextNode(String(v)));
						});
					}

					// add changes to cached attribute
					if (clientNode) {
						var newValue = clientNode[id];
						if (newValue != null) {
							if (bAppend) {
								if (bHash) {
									lang.mixin(newValue, value);
								} else {
									newValue = newValue.concat(value);
								}
							} else if (bRemove) {
								if (bHash) {
									csi.applyForEach(value, function(v, k) {
										csi.objectRemoveValue(newValue, v, true);
									});
								} else {
									array.forEach(value, function(v) {
										newValue.dele(v);
									});
								}
							} else {
								newValue = value;
							}
						} else if (bAppend) {
							newValue = value;
						}

						if (newValue) {
							xml.nodeAttributeSetValue(clientNode, id, newValue);
						}
					}
				} else {
					var strValue = String(value);

					// remove control characters as they are not valid XML characters
					// (see http://www.w3.org/TR/2008/REC-xml-20081126/#charsets)
					strValue = strValue.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "");

					// Always remove child nodes, we will add one if required.
					xml.removeChildren(node);

					if (strValue.indexOf('\n', 0) >= 0) {
						node.appendChild(doc.createTextNode(strValue));

						// Remove Value attribute, node could have be retrieved from cache.
						node.removeAttribute(xml.PROP_VALUE);
					} else {
						node.setAttribute(xml.PROP_VALUE, strValue);
					}

					// add changes to cached node
					if (clientNode) {
						xml.nodeAttributeSetValue(clientNode, id, value);
					}
				}
			}

			this.writer.setChanged(true);
		}
	});
});
